import { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import Select from "react-select";
import ConvertToLeadModal from "./PropsectToLeadModal";
import AlertDialog from "./AlertDialog";
import {
  CheckIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";
import { useLocation } from "react-router-dom";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Replace SOURCE_OPTIONS with:
const LEAD_SOURCE_CATEGORIES = [
  "Public Tender Platform",
  "Direct Invite",
  "Referral",
  "Outbound Prospecting",
  "Repeat Client",
];

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
  }),
};

const STAGE_OPTIONS = [
  { value: "Prospecting", label: "Prospecting" },
  { value: "Lost", label: "Lost" },
  { value: "Won", label: "Won" },
];

const SOURCE_OPTIONS = [
  { value: "Rocketreach", label: "Rocketreach" },
  { value: "Urban Toronto", label: "Urban Toronto" },
  { value: "Referral", label: "Referral" },
  { value: "Other", label: "Other" },
];

const COMPANY_TYPE_OPTIONS = [
  { value: "GC", label: "General Contractor" },
  { value: "Developer", label: "Developer" },
  { value: "Architect", label: "Architect" },
  { value: "Other", label: "Other" },
];

export default function ProspectDetails({
  prospect,
  setProspects,
  users,
  navigate,
}) {
  const [activeSubTab, setActiveSubTab] = useState("General");
  const location = useLocation();
  const [noteContent, setNoteContent] = useState("");
  const [noteSortOrder, setNoteSortOrder] = useState("desc");
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [activities, setActivities] = useState([]);

  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [newActivity, setNewActivity] = useState({
    title: "",
    dueDate: "",
    description: "",
    method: "email",
    assignedTo: null,
  });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isEditingActivity, setIsEditingActivity] = useState(false);
  const [editActivity, setEditActivity] = useState({
    title: "",
    dueDate: "",
    description: "",
    method: "email",
    status: "pending",
    assignedTo: null,
  });
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);
  const [editFormData, setEditFormData] = useState({
    projectName: "",
    companyName: "",
    email: "",
    phoneNumber: "",
    contactName: "",
    website: "",
    companyType: "Other",
    stage: "Prospecting",
    sourceCategory: "",
    estimatedProjectValue: "",
    address: {
      formattedAddress: "",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
  });

  const tabs = [
    { name: "General", current: activeSubTab === "General" },
    { name: "Notes", current: activeSubTab === "Notes" },
    { name: "Activities", current: activeSubTab === "Activities" },
  ];

  const formatDate = (date) =>
    date ? new Date(date).toISOString().split("T")[0] : "N/A";

  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = jwtDecode(token);
        const currentUser = users.find((user) => user.email === decoded.email);
        return currentUser?._id || null;
      }
      return null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const userOptions = users.map((user) => ({
    value: user._id,
    label: user.displayName || user.email,
  }));

  useEffect(() => {
    if (prospect) {
      setEditFormData({
        projectName: prospect.projectName || "",
        companyName: prospect.companyName || "",
        email: prospect.email || "",
        phoneNumber: prospect.phoneNumber || "",
        contactName: prospect.contactName || "",
        website: prospect.website || "",
        companyType: prospect.companyType || "Other",
        stage: prospect.stage || "Prospecting",
        sourceCategory: prospect.sourceCategory || "",
        estimatedProjectValue: prospect.estimatedProjectValue || "",
        address: prospect.address || {
          formattedAddress: "",
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
        },
      });
    }

    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      setNewActivity((prev) => ({ ...prev, assignedTo: currentUserId }));
    }
  }, [prospect, users]);

  const fetchTimeline = async () => {
    if (!prospect?._id) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/${prospect._id}/timeline`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setTimelineEvents(response.data);
    } catch (err) {
      console.error("Error fetching timeline:", err);
    }
  };

  const fetchActivities = async () => {
    if (!prospect?._id) return;
    try {
      const token = localStorage.getItem("token");
      // Assuming activities are part of prospect data
      setActivities(prospect.activities || []);
    } catch (err) {
      console.error("Error fetching activities:", err);
    }
  };

  useEffect(() => {
    if (prospect?._id) {
      fetchTimeline();
      fetchActivities();
    }
  }, [prospect?._id]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!prospect?._id) return;
    if (!noteContent.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Note content cannot be empty.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/${prospect._id}/notes`,
        { content: noteContent.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Get current user info for immediate display
      const currentUserId = getCurrentUserId();
      const currentUser = users.find((user) => user._id === currentUserId);

      // Create a properly formatted note with populated createdBy
      const newNote = {
        ...response.data.note,
        createdBy: response.data.note.createdBy?.displayName
          ? response.data.note.createdBy // Already populated from backend
          : {
              _id: currentUserId,
              displayName:
                currentUser?.displayName || currentUser?.email || "Unknown",
            },
      };

      setProspects((prev) =>
        prev.map((p) =>
          p._id === prospect._id
            ? {
                ...p,
                notes: [...(p.notes || []), newNote],
              }
            : p,
        ),
      );

      setNoteContent("");
      setShowNoteForm(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Note saved successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to save note: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleConversionError = (message) => {
    setAlert({
      open: true,
      type: "error",
      title: "Error",
      message: message,
      onConfirm: () => setAlert({ ...alert, open: false }),
    });
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!prospect?._id) return;

    if (
      !newActivity.title.trim() ||
      !newActivity.dueDate ||
      !newActivity.description.trim() ||
      !newActivity.assignedTo
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "All fields are required",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/${prospect._id}/activities`,
        newActivity,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setActivities((prev) => [...prev, response.data.activity]);
      setNewActivity({
        title: "",
        dueDate: "",
        description: "",
        method: "email",
        assignedTo: getCurrentUserId(),
      });
      setShowActivityForm(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Activity added successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to add activity: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleUpdateOpportunity = async (e) => {
    e.preventDefault();
    if (!prospect?._id) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/${prospect._id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setProspects((prev) =>
        prev.map((p) => (p._id === prospect._id ? response.data : p)),
      );
      setIsEditingGeneral(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Prospect updated successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to update prospect: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleConvertToLead = () => {
    setShowConvertModal(true);
  };

  const handleConversionSuccess = (data) => {
    // Update the prospect in the list to reflect "Won" status
    if (data.prospect) {
      setProspects((prev) =>
        prev.map((p) =>
          p._id === prospect._id ? { ...p, stage: data.prospect.stage } : p,
        ),
      );
    }

    setAlert({
      open: true,
      type: "success",
      title: "Success",
      message: "Prospect converted to lead successfully!",
      onConfirm: () => {
        setAlert({ ...alert, open: false });
        // Optionally navigate to the new lead
        // navigate(`/leads/${data.lead._id}`);
      },
    });

    fetchTimeline();
  };

  const toggleNoteSortOrder = () => {
    setNoteSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const sortedNotes = prospect?.notes
    ? [...prospect.notes].sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return noteSortOrder === "desc" ? dateB - dateA : dateA - dateB;
      })
    : [];

  const pendingActivities = activities
    .filter((act) => act.status === "pending")
    .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));

  const getEventStatus = (event, index) => {
    const now = new Date();
    const eventDate = new Date(event.timestamp);
    if (eventDate <= now) {
      return index === 0 ? "current" : "complete";
    }
    return "upcoming";
  };

  if (!prospect) {
    return (
      <div className="text-red-600 text-center py-4">Prospect not found</div>
    );
  }

  return (
    <div className="max-w-full mx-auto">
      <div className="bg-gray-200 shadow-xl rounded-xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-bold text-white bg-gray-700 rounded-md px-4 py-2">
            {prospect.projectName}
          </h1>
          <button
            onClick={() => navigate("/Prospects")}
            className="bg-gray-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-gray-500"
          >
            Back to Prospects
          </button>
        </div>

        <div className="mb-6">
          <div className="hidden sm:block">
            <nav
              aria-label="Tabs"
              className="isolate flex divide-x divide-gray-600 rounded-lg shadow-sm"
            >
              {tabs.map((tab, tabIdx) => (
                <button
                  key={tab.name}
                  onClick={() => setActiveSubTab(tab.name)}
                  aria-current={tab.current ? "page" : undefined}
                  className={classNames(
                    tab.current
                      ? "text-gray-200"
                      : "text-gray-400 hover:text-gray-200",
                    tabIdx === 0 ? "rounded-l-lg" : "",
                    tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                    "group relative min-w-0 flex-1 overflow-hidden bg-gray-700 px-4 py-4 text-center text-sm hover:bg-gray-700 focus:z-10 font-semibold",
                  )}
                >
                  <span>{tab.name}</span>
                  <span
                    aria-hidden="true"
                    className={classNames(
                      tab.current ? "bg-orange-400" : "bg-transparent",
                      "absolute inset-x-0 bottom-0 h-0.5",
                    )}
                  />
                </button>
              ))}
            </nav>
          </div>
        </div>

        {activeSubTab === "General" && (
          <div>
            <div className="bg-gray-700 p-6 rounded-lg shadow-lg">
              <div className="flex justify-between items-center mb-6">
                {!isEditingGeneral ? (
                  <>
                    <div></div>
                    <div className="flex space-x-4">
                      <button
                        onClick={handleConvertToLead}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-500"
                      >
                        Convert to Lead
                      </button>
                      <button
                        onClick={() => setIsEditingGeneral(true)}
                        className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                      >
                        Edit
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex space-x-4 ml-auto">
                    <button
                      onClick={() => setIsEditingGeneral(false)}
                      className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateOpportunity}
                      className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>

              {isEditingGeneral ? (
                <form onSubmit={handleUpdateOpportunity}>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Project Name
                      </label>
                      <input
                        type="text"
                        value={editFormData.projectName}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            projectName: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={editFormData.companyName}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            companyName: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            email: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={editFormData.phoneNumber}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            phoneNumber: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Contact Name
                      </label>
                      <input
                        type="text"
                        value={editFormData.contactName}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            contactName: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Website
                      </label>
                      <input
                        type="url"
                        value={editFormData.website}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            website: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Company Type
                      </label>
                      <Select
                        options={COMPANY_TYPE_OPTIONS}
                        value={COMPANY_TYPE_OPTIONS.find(
                          (option) => option.value === editFormData.companyType,
                        )}
                        onChange={(selected) =>
                          setEditFormData({
                            ...editFormData,
                            companyType: selected ? selected.value : "Other",
                          })
                        }
                        className="mt-1"
                        styles={customSelectStyles}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Stage
                      </label>
                      <Select
                        options={STAGE_OPTIONS}
                        value={STAGE_OPTIONS.find(
                          (option) => option.value === editFormData.stage,
                        )}
                        onChange={(selected) =>
                          setEditFormData({
                            ...editFormData,
                            stage: selected ? selected.value : "Prospecting",
                          })
                        }
                        className="mt-1"
                        styles={customSelectStyles}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Lead Source
                      </label>
                      <select
                        value={editFormData.sourceCategory}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            sourceCategory: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      >
                        <option value="">Select a source...</option>
                        {LEAD_SOURCE_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Estimated Project Value
                      </label>
                      <input
                        type="number"
                        value={editFormData.estimatedProjectValue}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            estimatedProjectValue: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-4 mt-4">
                    <button
                      type="button"
                      onClick={() => setIsEditingGeneral(false)}
                      className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                    >
                      Save
                    </button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Project Name
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      {prospect.projectName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Company Name
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      {prospect.companyName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Email
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      {prospect.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Phone Number
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      {prospect.phoneNumber || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Contact Name
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      {prospect.contactName || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Website
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      {prospect.website || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Company Type
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      {prospect.companyType || "Other"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Stage
                    </label>
                    <span
                      className={classNames(
                        prospect.stage === "Won"
                          ? "bg-green-100 text-green-700"
                          : prospect.stage === "Lost"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700",
                        "inline-flex items-center gap-x-1.5 rounded-full px-4 py-0.5 text-sm font-medium",
                      )}
                    >
                      <svg
                        viewBox="0 0 6 6"
                        aria-hidden="true"
                        className={classNames(
                          prospect.stage === "Won"
                            ? "fill-green-500"
                            : prospect.stage === "Lost"
                              ? "fill-red-500"
                              : "fill-yellow-500",
                          "size-1.5",
                        )}
                      >
                        <circle r={3} cx={3} cy={3} />
                      </svg>
                      {prospect.stage}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Lead Source
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      {prospect.sourceCategory || "—"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Estimated Project Value
                    </label>
                    <p className="mt-1 text-sm text-gray-300">
                      $
                      {prospect.estimatedProjectValue.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                </div>
              )}

              {pendingActivities.length > 0 && (
                <div className="mt-6">
                  <h2 className="text-lg font-semibold text-orange-400 mb-2">
                    Next Activities
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {pendingActivities.map((activity) => (
                      <button
                        key={activity._id}
                        onClick={() => {
                          setActiveSubTab("Activities");
                          setSelectedActivity(activity);
                        }}
                        className="inline-flex flex-col items-start px-4 py-2 rounded-lg text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200"
                      >
                        <span>{activity.title}</span>
                        <span className="text-xs mt-1">
                          Due: {formatDate(activity.dueDate)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSubTab === "Notes" && (
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-orange-400">Notes</h2>
              <div className="flex space-x-4">
                <button
                  onClick={toggleNoteSortOrder}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                >
                  Sort{" "}
                  {noteSortOrder === "desc" ? "Oldest First" : "Newest First"}
                </button>
                <button
                  onClick={() => setShowNoteForm(true)}
                  className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                >
                  Add Note
                </button>
              </div>
            </div>

            {showNoteForm && (
              <form onSubmit={handleAddNote} className="mb-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Add a Note
                  </label>
                  <textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    rows={4}
                    placeholder="Enter your note here..."
                  />
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNoteForm(false);
                      setNoteContent("");
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                  >
                    Save Note
                  </button>
                </div>
              </form>
            )}

            <div>
              {sortedNotes.length === 0 ? (
                <p className="text-gray-300">No notes available.</p>
              ) : (
                <ul className="divide-y divide-gray-600">
                  {sortedNotes.map((note, index) => (
                    <li key={index} className="py-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm text-gray-200">
                            {note.content}
                          </p>
                          <p className="text-xs text-gray-300 mt-1">
                            By {note.createdBy?.displayName || "Unknown"} on{" "}
                            {formatDate(note.createdAt)}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {activeSubTab === "Activities" && (
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-orange-400">
                Activities
              </h2>
              <button
                onClick={() => {
                  setShowActivityForm(true);
                  setIsEditingActivity(false);
                  setNewActivity({
                    title: "",
                    dueDate: "",
                    description: "",
                    method: "email",
                    assignedTo: getCurrentUserId(),
                  });
                }}
                className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
              >
                Add Activity
              </button>
            </div>

            {showActivityForm && (
              <form onSubmit={handleAddActivity} className="mb-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Title
                    </label>
                    <input
                      type="text"
                      value={newActivity.title}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          title: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      placeholder="Enter activity title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Due Date
                    </label>
                    <input
                      type="datetime-local"
                      value={newActivity.dueDate}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          dueDate: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-400">
                      Description
                    </label>
                    <textarea
                      value={newActivity.description}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          description: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      rows={4}
                      placeholder="Enter activity description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Method
                    </label>
                    <select
                      value={newActivity.method}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          method: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    >
                      <option value="email">Email</option>
                      <option value="call">Call</option>
                      <option value="in-person">In-Person</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Assigned To
                    </label>
                    <Select
                      options={userOptions}
                      value={userOptions.find(
                        (option) => option.value === newActivity.assignedTo,
                      )}
                      onChange={(selected) =>
                        setNewActivity({
                          ...newActivity,
                          assignedTo: selected ? selected.value : null,
                        })
                      }
                      placeholder="Select user"
                      className="mt-1"
                      styles={customSelectStyles}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-4 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowActivityForm(false);
                      setNewActivity({
                        title: "",
                        dueDate: "",
                        description: "",
                        method: "email",
                        assignedTo: getCurrentUserId(),
                      });
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                  >
                    Save Activity
                  </button>
                </div>
              </form>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">
                All Activities
              </h3>
              {activities.length === 0 ? (
                <p className="text-gray-300">No activities available.</p>
              ) : (
                <ul className="divide-y divide-gray-600">
                  {activities
                    .sort(
                      (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
                    )
                    .map((activity) => (
                      <li
                        key={activity._id}
                        className="py-4 cursor-pointer hover:bg-gray-600"
                        onClick={() => {
                          setSelectedActivity(activity);
                          setIsEditingActivity(false);
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-200">
                              {activity.title}
                            </p>
                            <p className="text-sm text-gray-300">
                              {activity.description}
                            </p>
                            <p className="text-xs text-gray-300 mt-1">
                              {activity.method} | {activity.status} | Due:{" "}
                              {formatDate(activity.dueDate)}
                            </p>
                          </div>
                          <span
                            className={classNames(
                              activity.status === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700",
                              "inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
                            )}
                          >
                            {activity.status.charAt(0).toUpperCase() +
                              activity.status.slice(1)}
                          </span>
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <ConvertToLeadModal
          isOpen={showConvertModal}
          onClose={() => setShowConvertModal(false)}
          prospect={prospect}
          onSuccess={handleConversionSuccess}
          onError={handleConversionError}
        />

        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert({ ...alert, open: false })}
          onConfirm={alert.onConfirm}
          showCancel={alert.showCancel}
        />
      </div>
    </div>
  );
}
